/* eslint-disable no-loop-func */
// src/services/imageLoader.js
// 이미지 다운로드 및 캐싱을 담당하는 서비스 - 성능 최적화 버전
// 🔐 [신규 추가] 보안 로그 제어 시스템 적용
// 🔧 [수정] 실제 다운로드 상태 확인 메서드 추가

// 🔐 [신규 추가] 보안 로그 제어 시스템 import
import { debugLog, errorLog } from '../utils/environment';

const CACHE_NAME = 'painori-images-v1';
const CACHE_EXPIRY = 30 * 24 * 60 * 60 * 1000; // 30일
const DOWNLOAD_STATE_KEY = 'painori-download-state';
const MAX_CACHE_SIZE = 100 * 1024 * 1024; // 100MB 제한
const MAX_CONCURRENT_DOWNLOADS = 3; // 동시 다운로드 제한

class ImageLoader {
  constructor() {
    this.downloadProgress = {};
    this.downloadCallbacks = {};

    // [추가] 메모리 관리를 위한 blob URL 추적
    this.blobUrls = new Map();

    // [추가] 프리로드 상태 추적
    this.preloadStatus = new Map();
    this.preloadQueue = [];
    this.activePreloads = 0;

    // [추가] 캐시 통계
    this.cacheStats = {
      hits: 0,
      misses: 0,
      totalSize: 0,
      downloadTime: new Map()
    };

    // [추가] LRU 캐시 추적
    this.cacheAccessTime = new Map();

    // 초기화 시 저장된 다운로드 상태 복구
    this.recoverDownloadState();

    // [추가] 캐시 크기 계산
    this.calculateCacheSize();
  }

  // [추가] 캐시 크기 계산
  async calculateCacheSize() {
    try {
      const size = await this.getCacheSize();
      this.cacheStats.totalSize = size;
      debugLog(`[ImageLoader] 현재 캐시 크기: ${(size / 1024 / 1024).toFixed(2)}MB`);
    } catch (error) {
      errorLog('[ImageLoader] 캐시 크기 계산 실패:', error);
    }
  }

  // 다운로드 상태 복구
  recoverDownloadState() {
    try {
      const savedState = localStorage.getItem(DOWNLOAD_STATE_KEY);
      if (savedState) {
        const state = JSON.parse(savedState);
        const now = Date.now();

        // 5분 이내의 다운로드 상태만 복구
        Object.entries(state).forEach(([key, data]) => {
          if (now - data.timestamp < 5 * 60 * 1000) {
            this.downloadProgress[key] = data.progress;
            debugLog(`[ImageLoader] 다운로드 상태 복구: ${key} (${data.progress}%)`);
          }
        });

        // 오래된 상태는 정리
        this.saveDownloadState();
      }
    } catch (error) {
      errorLog('[ImageLoader] 다운로드 상태 복구 실패:', error);
    }
  }

  // 다운로드 상태 저장
  saveDownloadState() {
    try {
      const state = {};
      const now = Date.now();

      Object.entries(this.downloadProgress).forEach(([key, progress]) => {
        state[key] = {
          progress,
          timestamp: now
        };
      });

      localStorage.setItem(DOWNLOAD_STATE_KEY, JSON.stringify(state));
    } catch (error) {
      errorLog('[ImageLoader] 다운로드 상태 저장 실패:', error);
    }
  }

  // 캐시 가능 여부 확인
  async isCacheAvailable() {
    return 'caches' in window;
  }

  // 테마가 캐시에 있는지 확인
  async isThemeCached(themeId) {
    if (!await this.isCacheAvailable()) return false;

    try {
      const cache = await caches.open(CACHE_NAME);
      const themeKey = `theme-${themeId}`;
      const response = await cache.match(themeKey);

      if (!response) return false;

      // 캐시 유효성 확인
      const cacheData = await response.json();
      const now = Date.now();

      if (now - cacheData.timestamp > CACHE_EXPIRY) {
        await cache.delete(themeKey);
        return false;
      }

      return true;
    } catch (error) {
      errorLog('캐시 확인 실패:', error);
      return false;
    }
  }

  // [최적화] Cloudflare 무료 리사이징을 활용한 이미지 URL 생성
  getOptimizedImageUrl(stageId, imageName, options = {}) {
    const { isThumbnail = false, priority = 'normal', width, height } = options;

    // [안전성] 입력값 검증
    if (!stageId || !imageName) {
      errorLog('[ImageLoader] 잘못된 매개변수:', { stageId, imageName });
      return `https://assets.spot.painori.com/${stageId || '10000'}/${imageName || '00.png'}`;
    }

    // 기본 URL
    let baseUrl = `https://assets.spot.painori.com/${stageId}/${imageName}`;

    // 썸네일인 경우 최적화
    if (isThumbnail || imageName === '00.png') {
      const screenWidth = window.innerWidth || 1024; // 기본값 설정
      const thumbWidth = width || (screenWidth < 768 ? 120 : screenWidth < 1024 ? 240 : 320);
      return `${baseUrl}?width=${thumbWidth}&format=webp&quality=80`;
    }

    // 일반 이미지 최적화
    const quality = priority === 'high' ? 90 : 80;
    const format = this.supportsWebP() ? 'webp' : 'png';

    if (width || height) {
      const resizeParams = [];
      if (width) resizeParams.push(`width=${width}`);
      if (height) resizeParams.push(`height=${height}`);
      return `${baseUrl}?${resizeParams.join('&')}&format=${format}&quality=${quality}`;
    }

    return `${baseUrl}?format=${format}&quality=${quality}`;
  }

  // WebP 지원 확인
  supportsWebP() {
    const canvas = document.createElement('canvas');
    return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  }

  // [최적화] 이미지 URL 가져오기 - Cloudflare 무료 리사이징 활용
  async getImageUrl(stageId, imageName, options = {}) {
    const startTime = Date.now();
    const cacheKey = `${stageId}/${imageName}`;

    debugLog(`[캐시 체크] ${cacheKey} 요청`);

    // 🚀 [최적화] Cloudflare 무료 리사이징으로 성능 개선
    const imageUrl = this.getOptimizedImageUrl(stageId, imageName, options);

    // 🔧 [최적화] Service Worker와 중복 방지 - Cloudflare 캐시 우선 활용
    if (!await this.isCacheAvailable()) {
      debugLog(`❌ [캐시 미스] ${imageName} - 캐시 사용 불가, Cloudflare에서 직접 로드`);
      this.cacheStats.misses++;
      return imageUrl;
    }

    try {
      const cache = await caches.open(CACHE_NAME);
      const cachedResponse = await cache.match(imageUrl);

      if (cachedResponse) {
        debugLog(`✅ [캐시 히트] ${imageName} - Service Worker 캐시에서 로드`);
        this.cacheStats.hits++;

        // [추가] LRU 업데이트
        this.cacheAccessTime.set(imageUrl, Date.now());

        // [수정] blob URL 생성 및 관리
        const blob = await cachedResponse.blob();
        const blobUrl = URL.createObjectURL(blob);

        // 이전 blob URL이 있으면 해제
        if (this.blobUrls.has(cacheKey)) {
          URL.revokeObjectURL(this.blobUrls.get(cacheKey));
        }
        this.blobUrls.set(cacheKey, blobUrl);

        const loadTime = Date.now() - startTime;
        debugLog(`⏱️ [로드 시간] ${imageName}: ${loadTime}ms`);

        return blobUrl;
      }

      debugLog(`❌ [캐시 미스] ${imageName} - Cloudflare에서 다운로드 필요`);
      this.cacheStats.misses++;
      return imageUrl;
    } catch (error) {
      errorLog('이미지 URL 가져오기 실패:', error);
      debugLog(`❌ [캐시 에러] ${imageName} - 에러로 인해 Cloudflare에서 로드`);
      this.cacheStats.misses++;
      return imageUrl;
    }
  }

  // [추가] 스테이지 이미지 프리로드
  async preloadStageImages(stageId) {
    const preloadKey = `stage-${stageId}`;

    // 이미 프리로드 중이거나 완료된 경우 스킵
    if (this.preloadStatus.has(preloadKey)) {
      const status = this.preloadStatus.get(preloadKey);
      if (status === 'loading' || status === 'completed') {
        debugLog(`[프리로드] ${stageId} 이미 처리중 또는 완료`);
        return;
      }
    }

    this.preloadStatus.set(preloadKey, 'loading');
    debugLog(`[프리로드 시작] 스테이지 ${stageId}`);

    try {
      const cache = await this.isCacheAvailable() ? await caches.open(CACHE_NAME) : null;
      const imagePromises = [];

      // 기본 이미지 (00.png)와 차이점 이미지들 (01~05.png)
      for (let i = 0; i <= 5; i++) {
        const imageName = `0${i}.png`;
        const imageUrl = `https://assets.spot.painori.com/${stageId}/${imageName}`;

        // 캐시 확인
        if (cache) {
          const cached = await cache.match(imageUrl);
          if (cached) {
            debugLog(`✅ [프리로드] ${imageName} 이미 캐시됨`);
            continue;
          }
        }

        // 다운로드 필요
        imagePromises.push(
          fetch(imageUrl)
            .then(response => {
              if (!response.ok) throw new Error(`Failed to fetch ${imageUrl}`);
              return response.blob();
            })
            .then(async blob => {
              if (cache) {
                const response = new Response(blob);
                await cache.put(imageUrl, response);
                debugLog(`✅ [프리로드 캐시] ${imageName}`);
              }
            })
            .catch(error => {
              errorLog(`❌ [프리로드 실패] ${imageName}:`, error);
            })
        );
      }

      // txt 파일도 프리로드
      const txtUrl = `https://assets.spot.painori.com/${stageId}/${stageId}.txt`;
      if (cache) {
        const txtCached = await cache.match(txtUrl);
        if (!txtCached) {
          imagePromises.push(
            fetch(txtUrl)
              .then(response => response.text())
              .then(async text => {
                const response = new Response(text);
                await cache.put(txtUrl, response);
                debugLog(`✅ [프리로드 캐시] ${stageId}.txt`);
              })
              .catch(error => {
                errorLog(`❌ [프리로드 실패] txt 파일:`, error);
              })
          );
        }
      }

      await Promise.all(imagePromises);
      this.preloadStatus.set(preloadKey, 'completed');
      debugLog(`✅ [프리로드 완료] 스테이지 ${stageId}`);

    } catch (error) {
      errorLog(`❌ [프리로드 에러] 스테이지 ${stageId}:`, error);
      this.preloadStatus.set(preloadKey, 'failed');
    }
  }

  // [추가] 여러 스테이지 일괄 프리로드
  async preloadMultipleStages(stageIds, priority = false) {
    if (!Array.isArray(stageIds) || stageIds.length === 0) return;

    debugLog(`[일괄 프리로드] ${stageIds.length}개 스테이지 대기열 추가`);

    // 우선순위가 높으면 큐 앞에 추가
    if (priority) {
      this.preloadQueue.unshift(...stageIds);
    } else {
      this.preloadQueue.push(...stageIds);
    }

    // 프리로드 처리 시작
    this.processPreloadQueue();
  }

  // [추가] 프리로드 큐 처리
  async processPreloadQueue() {
    while (this.preloadQueue.length > 0 && this.activePreloads < MAX_CONCURRENT_DOWNLOADS) {
      const stageId = this.preloadQueue.shift();
      this.activePreloads++;

      this.preloadStageImages(stageId)
        .finally(() => {
          this.activePreloads--;
          // 다음 항목 처리
          if (this.preloadQueue.length > 0) {
            this.processPreloadQueue();
          }
        });
    }
  }

  // [추가] 프리로드 진행률 확인
  getPreloadProgress(stageId) {
    const preloadKey = `stage-${stageId}`;
    return this.preloadStatus.get(preloadKey) || 'not-started';
  }

  // [추가] 스테이지 리소스 정리
  cleanupStage(stageId) {
    debugLog(`[정리] 스테이지 ${stageId} 리소스 해제`);

    // blob URL 해제
    let cleanedCount = 0;
    for (const [key, url] of this.blobUrls) {
      if (key.startsWith(`${stageId}/`)) {
        URL.revokeObjectURL(url);
        this.blobUrls.delete(key);
        cleanedCount++;
      }
    }

    if (cleanedCount > 0) {
      debugLog(`✅ [정리 완료] ${cleanedCount}개 blob URL 해제`);
    }
  }

  // [추가] 모든 blob URL 정리
  cleanupAllBlobs() {
    debugLog(`[정리] 모든 blob URL 해제 (${this.blobUrls.size}개)`);

    for (const [, url] of this.blobUrls) {
      URL.revokeObjectURL(url);
    }
    this.blobUrls.clear();
  }

  // [추가] LRU 기반 캐시 정리
  async cleanupCacheIfNeeded() {
    if (this.cacheStats.totalSize < MAX_CACHE_SIZE) return;

    debugLog('[캐시 정리] 용량 초과, LRU 정리 시작');

    try {
      const cache = await caches.open(CACHE_NAME);
      const keys = await cache.keys();

      // 접근 시간 기준 정렬
      const sortedKeys = keys.sort((a, b) => {
        const timeA = this.cacheAccessTime.get(a.url) || 0;
        const timeB = this.cacheAccessTime.get(b.url) || 0;
        return timeA - timeB;
      });

      // 오래된 항목부터 삭제 (전체의 20%)
      const deleteCount = Math.floor(sortedKeys.length * 0.2);
      for (let i = 0; i < deleteCount; i++) {
        await cache.delete(sortedKeys[i]);
        this.cacheAccessTime.delete(sortedKeys[i].url);
      }

      debugLog(`✅ [캐시 정리] ${deleteCount}개 항목 삭제`);
      await this.calculateCacheSize();

    } catch (error) {
      errorLog('[캐시 정리 실패]:', error);
    }
  }

  // [추가] 캐시 통계 조회
  getCacheStats() {
    const hitRate = this.cacheStats.hits + this.cacheStats.misses > 0
      ? (this.cacheStats.hits / (this.cacheStats.hits + this.cacheStats.misses) * 100).toFixed(2)
      : 0;

    return {
      hits: this.cacheStats.hits,
      misses: this.cacheStats.misses,
      hitRate: `${hitRate}%`,
      totalSize: `${(this.cacheStats.totalSize / 1024 / 1024).toFixed(2)}MB`,
      blobCount: this.blobUrls.size,
      preloadQueueSize: this.preloadQueue.length
    };
  }

  // 부분 다운로드된 이미지 확인
  async getDownloadedImages(startStageId, endStageId) {
    if (!await this.isCacheAvailable()) return [];

    const downloadedImages = [];
    const cache = await caches.open(CACHE_NAME);

    for (let stageId = startStageId; stageId <= endStageId; stageId++) {
      for (let i = 0; i <= 5; i++) {
        const imageName = `0${i}.png`;
        const imageUrl = `https://assets.spot.painori.com/${stageId}/${imageName}`;

        const cachedResponse = await cache.match(imageUrl);
        if (cachedResponse) {
          downloadedImages.push(`${stageId}/${imageName}`);
        }
      }
    }

    return downloadedImages;
  }

  // [수정] 테마 다운로드 - LRU 정리 추가
  async downloadTheme(themeId, startStageId, endStageId, onProgress) {
    // 캐시 용량 확인 및 정리
    await this.cleanupCacheIfNeeded();

    const downloadKey = `download-${themeId}`;

    // 이미 다운로드 중인 경우 처리 개선
    if (this.downloadProgress[downloadKey] !== undefined) {
      debugLog(`[ImageLoader] 이미 다운로드 중: ${themeId} (${this.downloadProgress[downloadKey]}%)`);

      // 진행 중인 다운로드에 새 콜백 연결
      if (onProgress && this.downloadProgress[downloadKey] > 0) {
        // 현재 진행률 즉시 전달
        onProgress(this.downloadProgress[downloadKey]);

        // 콜백 등록
        if (!this.downloadCallbacks[downloadKey]) {
          this.downloadCallbacks[downloadKey] = [];
        }
        this.downloadCallbacks[downloadKey].push({ onProgress });
      }

      return new Promise((resolve) => {
        if (!this.downloadCallbacks[downloadKey]) {
          this.downloadCallbacks[downloadKey] = [];
        }
        this.downloadCallbacks[downloadKey].push({ resolve });
      });
    }

    this.downloadProgress[downloadKey] = 0;
    this.downloadCallbacks[downloadKey] = [];

    // 다운로드 상태 저장
    this.saveDownloadState();

    debugLog(`[다운로드 시작] 테마 ${themeId} (스테이지 ${startStageId}-${endStageId})`);

    try {
      const totalStages = endStageId - startStageId + 1;
      const imagesPerStage = 6; // 00.png ~ 05.png
      const totalImages = totalStages * imagesPerStage + totalStages; // 이미지 + txt 파일
      let downloadedImages = 0;

      const cache = await this.isCacheAvailable() ? await caches.open(CACHE_NAME) : null;

      // 이미 다운로드된 이미지 확인
      const alreadyDownloaded = await this.getDownloadedImages(startStageId, endStageId);
      downloadedImages = alreadyDownloaded.length;

      if (downloadedImages > 0) {
        const initialProgress = Math.round((downloadedImages / totalImages) * 100);
        this.downloadProgress[downloadKey] = initialProgress;
        debugLog(`[ImageLoader] 부분 다운로드 감지: ${downloadedImages}/${totalImages} (${initialProgress}%)`);

        if (onProgress) {
          onProgress(initialProgress);
        }
      }

      // 모든 스테이지의 이미지 다운로드
      // eslint-disable-next-line no-loop-func
      for (let stageId = startStageId; stageId <= endStageId; stageId++) {
        const stagePromises = [];

        // 각 스테이지의 이미지들 (00.png ~ 05.png)
        for (let i = 0; i <= 5; i++) {
          const imageName = `0${i}.png`;
          const imageUrl = `https://assets.spot.painori.com/${stageId}/${imageName}`;

          // 이미 다운로드된 이미지는 건너뛰기
          if (alreadyDownloaded.includes(`${stageId}/${imageName}`)) {
            continue;
          }

          stagePromises.push(
            fetch(imageUrl)
              .then(response => {
                if (!response.ok) throw new Error(`Failed to fetch ${imageUrl}`);
                return response.blob();
              })
              .then(async blob => {
                // 캐시에 저장
                if (cache) {
                  const response = new Response(blob);
                  await cache.put(imageUrl, response);

                  // [추가] LRU 업데이트
                  this.cacheAccessTime.set(imageUrl, Date.now());

                  debugLog(`✅ [캐시 저장] ${stageId}/${imageName}`);
                }

                downloadedImages++;
                const progress = Math.round((downloadedImages / totalImages) * 100);
                this.downloadProgress[downloadKey] = progress;

                // 상태 저장
                this.saveDownloadState();

                if (onProgress) {
                  onProgress(progress);
                }

                // 등록된 모든 콜백에 진행률 전달
                this.downloadCallbacks[downloadKey].forEach(callback => {
                  if (callback.onProgress) {
                    callback.onProgress(progress);
                  }
                });
              })
              .catch(error => {
                errorLog(`❌ [다운로드 실패] ${stageId}/${imageName}:`, error);
                downloadedImages++;
              })
          );
        }

        // differences.txt 파일도 다운로드
        const txtUrl = `https://assets.spot.painori.com/${stageId}/${stageId}.txt`;

        // txt 파일도 캐시 확인
        const txtCached = cache ? await cache.match(txtUrl) : null;
        if (!txtCached) {
          stagePromises.push(
            fetch(txtUrl)
              .then(response => response.text())
              .then(async text => {
                if (cache) {
                  const response = new Response(text);
                  await cache.put(txtUrl, response);
                  debugLog(`✅ [캐시 저장] ${stageId}/${stageId}.txt`);
                }

                downloadedImages++;
                const progress = Math.round((downloadedImages / totalImages) * 100);
                this.downloadProgress[downloadKey] = progress;

                // 상태 저장
                this.saveDownloadState();

                if (onProgress) {
                  onProgress(progress);
                }
              })
              .catch(error => {
                errorLog(`❌ [텍스트 파일 다운로드 실패] ${txtUrl}:`, error);
                downloadedImages++;
              })
          );
        }

        await Promise.all(stagePromises);
      }

      // 테마 메타데이터 저장
      if (cache) {
        const themeKey = `theme-${themeId}`;
        const metadata = {
          themeId,
          timestamp: Date.now(),
          startStageId,
          endStageId
        };
        await cache.put(themeKey, new Response(JSON.stringify(metadata)));
        debugLog(`✅ [테마 메타데이터 저장] ${themeId}`);
      }

      // 다운로드 완료
      debugLog(`✅ [다운로드 완료] 테마 ${themeId}`);
      delete this.downloadProgress[downloadKey];

      // 다운로드 상태 저장
      this.saveDownloadState();

      // [추가] 캐시 크기 재계산
      await this.calculateCacheSize();

      // 대기 중인 콜백 실행
      this.downloadCallbacks[downloadKey].forEach(callback => {
        if (callback.resolve) callback.resolve(true);
      });
      delete this.downloadCallbacks[downloadKey];

      return true;

    } catch (error) {
      errorLog('❌ [테마 다운로드 실패]:', error);
      delete this.downloadProgress[downloadKey];

      // 다운로드 상태 저장
      this.saveDownloadState();

      // 대기 중인 콜백 실행
      this.downloadCallbacks[downloadKey].forEach(callback => {
        if (callback.resolve) callback.resolve(false);
      });
      delete this.downloadCallbacks[downloadKey];

      return false;
    }
  }

  // 캐시 삭제
  async clearThemeCache(themeId) {
    if (!await this.isCacheAvailable()) return;

    try {
      const cache = await caches.open(CACHE_NAME);
      const keys = await cache.keys();

      // 해당 테마의 모든 이미지 삭제 (assets.spot.painori.com URL 패턴 반영)
      const deletePromises = keys
        .filter(request => request.url.includes('assets.spot.painori.com') &&
          request.url.includes(themeId))
        .map(request => cache.delete(request));

      // 테마 메타데이터 삭제
      await cache.delete(`theme-${themeId}`);

      await Promise.all(deletePromises);

      debugLog(`✅ [캐시 삭제] 테마 ${themeId} 캐시 삭제 완료`);

      // [추가] 캐시 크기 재계산
      await this.calculateCacheSize();
    } catch (error) {
      errorLog('❌ [캐시 삭제 실패]:', error);
    }
  }

  // 전체 캐시 삭제
  async clearAllCache() {
    if (!await this.isCacheAvailable()) return;

    try {
      await caches.delete(CACHE_NAME);

      // 다운로드 상태도 삭제
      localStorage.removeItem(DOWNLOAD_STATE_KEY);
      this.downloadProgress = {};
      this.downloadCallbacks = {};

      // [추가] 모든 blob URL 정리
      this.cleanupAllBlobs();

      // [추가] 통계 초기화
      this.cacheStats = {
        hits: 0,
        misses: 0,
        totalSize: 0,
        downloadTime: new Map()
      };
      this.cacheAccessTime.clear();
      this.preloadStatus.clear();
      this.preloadQueue = [];

      debugLog('✅ [전체 캐시 삭제] 완료');
    } catch (error) {
      errorLog('❌ [전체 캐시 삭제 실패]:', error);
    }
  }

  // 캐시 크기 확인
  async getCacheSize() {
    if (!await this.isCacheAvailable()) return 0;

    try {
      const cache = await caches.open(CACHE_NAME);
      const keys = await cache.keys();
      let totalSize = 0;

      for (const request of keys) {
        const response = await cache.match(request);
        if (response) {
          const blob = await response.blob();
          totalSize += blob.size;
        }
      }

      return totalSize;
    } catch (error) {
      errorLog('캐시 크기 확인 실패:', error);
      return 0;
    }
  }

  // 🆕 다운로드 상태 확인 메서드들 (중복 방지용)

  // 특정 테마가 다운로드 중인지 확인
  isThemeDownloading(themeId) {
    const downloadKey = `download-${themeId}`;
    return this.downloadProgress[downloadKey] !== undefined;
  }

  // 특정 테마의 다운로드 진행률 확인
  getThemeDownloadProgress(themeId) {
    const downloadKey = `download-${themeId}`;
    return this.downloadProgress[downloadKey] || 0;
  }

  // 🔧 [핵심 추가] 실제로 다운로드가 진행 중인지 확인하는 메서드
  isActuallyDownloading(themeId) {
    const downloadKey = `download-${themeId}`;
    // downloadProgress에 키가 존재하고 값이 숫자인지 확인
    const progressValue = this.downloadProgress[downloadKey];
    const hasProgress = typeof progressValue === 'number' && progressValue >= 0;
    const hasCallbacks = this.downloadCallbacks[downloadKey] && this.downloadCallbacks[downloadKey].length > 0;

    debugLog(`[ImageLoader] isActuallyDownloading(${themeId}): progress=${hasProgress}, callbacks=${hasCallbacks}, value=${progressValue}`);

    return !!(hasProgress || hasCallbacks);
  }

  // 현재 다운로드 중인 모든 테마 목록 조회
  getDownloadingThemes() {
    const downloading = [];
    Object.keys(this.downloadProgress).forEach(key => {
      if (key.startsWith('download-')) {
        const themeId = key.replace('download-', '');
        downloading.push({
          themeId,
          progress: this.downloadProgress[key]
        });
      }
    });
    return downloading;
  }
}

// 싱글톤 인스턴스 생성
const imageLoader = new ImageLoader();

export default imageLoader;